import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { CopyRow } from '../components/CopyRow';
import {
  cancelDeposit,
  createDeposit,
  createOpenCryptoDeposit,
  createWithdrawal,
  fetchActiveDeposit,
  fetchDepositQr,
  fetchMyDeposits,
  fetchMyWithdrawals,
  fetchPaymentMethods,
  fetchTransactions,
  fetchWalletSnapshot,
  formatMinor,
  newIdempotencyKey,
  paymentErrorMessage,
  uploadDepositReceipt,
  type CryptoPairInfo,
  type DepositResponse,
  type PaymentMethodInfo,
  type TransactionItem,
  type WithdrawResponse,
  type WalletSnapshot,
} from '../api/payments';
import {
  createTonConnectDeposit,
  fetchTonConnectStatus,
} from '../api/tonConnect';
import { CryptoInstantDepositPanel } from '../components/CryptoInstantDepositPanel';
import {
  fetchPlayOptions,
  savePreferences,
  type PaymentRegionOption,
  type PlayCurrency,
  type PlayOptions,
} from '../api/preferences';
import {
  aznMinorToPlayMinorCeil,
  aznMinorToPlayMinorFloor,
  playMajorToAznMinor,
} from '../lib/fxDisplay';
import {
  hasManualPaymentRegion,
  markPaymentRegionManual,
  suggestedRegionForCurrency,
} from '../lib/currencyRegion';

const REGION_DISPLAY_CURRENCY: Record<string, PlayCurrency> = {
  AZ: 'AZN',
  RU: 'RUB',
  US: 'USD',
  EU: 'EUR',
};

const CURRENCY_SYMBOL: Record<string, string> = {
  AZN: '₼',
  RUB: '₽',
  USD: '$',
  EUR: '€',
};

type CashierTab = 'deposit' | 'withdraw' | 'history';

const DEPOSIT_LABELS: Record<string, string> = {
  card_to_card: 'cardToCard',
  m10: 'm10',
  terminal: 'terminal',
  crypto: 'crypto',
};

const WITHDRAW_LABELS: Record<string, string> = {
  bank_card: 'bankCardWithdraw',
  m10: 'm10Withdraw',
  crypto: 'cryptoWithdraw',
};

// UI-only cashier preview. No transaction or balance changes are sent.
const CASHIER_PREVIEW_MODE = false;

// Preview-only fallbacks when API pairs are empty. Never invent unfunded pairs.
const CRYPTO_SHOWCASE = [
  ['GRAM', 'TON'], ['USDT', 'TRC20'], ['ETH', 'ERC20'], ['BTC', 'BITCOIN'],
  ['USDT', 'ERC20'], ['USDC', 'ERC20'], ['SOL', 'SOLANA'], ['BNB', 'BEP20'],
  ['LTC', 'LITECOIN'], ['USDC', 'SOLANA'],
] as const;

/** TonPlay-style cashier order. Backend allowlist is source of truth; FE sorts the same way. */
const CRYPTO_PAIR_PRIORITY: ReadonlyArray<readonly [string, string]> = [
  ['GRAM', 'TON'],
  ['USDT', 'TRC20'],
  ['USDT', 'TON'],
  ['USDT', 'BEP20'],
  ['TRX', 'TRC20'],
  ['ETH', 'ERC20'],
  ['BTC', 'BITCOIN'],
  ['USDT', 'ERC20'],
  ['USDC', 'ERC20'],
  ['SOL', 'SOLANA'],
  ['BNB', 'BEP20'],
  ['LTC', 'LITECOIN'],
  ['USDC', 'SOLANA'],
  ['USDC', 'BEP20'],
];

const CRYPTO_PAIR_HIDDEN = new Set([
  'USDC:ARBITRUM',
  'USDC:BASE',
  'USDC:POLYGON',
  'USDT:ARBITRUM',
  'USDT:POLYGON',
  'USDT:SOLANA',
  'ETH:ARBITRUM',
  'ETH:BASE',
  'DOGE:DOGECOIN',
  'TON:TON',
]);

function isHiddenCryptoPair(asset: string, network: string): boolean {
  return CRYPTO_PAIR_HIDDEN.has(`${asset}:${network}`);
}

function sortCryptoPairs<T extends { asset: string; network: string }>(pairs: T[]): T[] {
  const rank = (p: { asset: string; network: string }) => {
    const idx = CRYPTO_PAIR_PRIORITY.findIndex(([a, n]) => a === p.asset && n === p.network);
    return idx === -1 ? CRYPTO_PAIR_PRIORITY.length + 1 : idx;
  };
  return [...pairs].sort((a, b) => {
    const d = rank(a) - rank(b);
    if (d !== 0) return d;
    return a.asset.localeCompare(b.asset) || a.network.localeCompare(b.network);
  });
}

function coinIcon(asset: string) {
  const key = asset.toUpperCase();
  const logo: Record<string, React.ReactNode> = {
    USDT: <svg viewBox="0 0 64 64" aria-hidden="true"><path fill="currentColor" d="M11 16h42v10H37v7.6c10.8.5 18.8 2.7 18.8 5.2 0 3.2-10.6 5.7-23.8 5.7S8.2 42 8.2 38.8c0-2.6 8-4.7 18.8-5.2V26H11V16zm16 20.5c-8.9.4-15 1.7-15 3.2 0 1.9 9 3.5 20 3.5s20-1.6 20-3.5c0-1.5-6.1-2.8-15-3.2V51H27V36.5z"/></svg>,
    BTC: <svg viewBox="0 0 64 64" aria-hidden="true"><text x="32" y="48" textAnchor="middle" fill="currentColor" fontFamily="Arial,sans-serif" fontWeight="900" fontSize="53">₿</text></svg>,
    ETH: <svg viewBox="0 0 64 64" aria-hidden="true"><path fill="#fff" d="M32 5 16 33l16 9 16-9L32 5Z"/><path fill="#d4d9ff" d="M32 5v37l16-9L32 5Z"/><path fill="#fff" d="m16 38 16 21 16-21-16 9-16-9Z"/><path fill="#d4d9ff" d="M32 47v12l16-21-16 9Z"/></svg>,
    TRX: <svg viewBox="0 0 64 64" aria-hidden="true"><path d="M10 10 54 17 43 53 10 10 24 38 54 17M24 38l19 15" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round"/></svg>,
    GRAM: <svg viewBox="0 0 64 64" aria-hidden="true"><path fill="currentColor" d="M32 7 38.9 25.1 57 32l-18.1 6.9L32 57l-6.9-18.1L7 32l18.1-6.9L32 7Z"/></svg>,
    TON: <svg viewBox="0 0 64 64" aria-hidden="true"><path d="M11 16h42L32 53 11 16Zm21 37V16" fill="none" stroke="currentColor" strokeWidth="4" strokeLinejoin="round"/></svg>,
  };
  return <span aria-hidden="true" className={`cashier-coin cashier-coin-${key.toLowerCase()}`}>{logo[key] || key.slice(0, 2)}</span>;
}

function methodIcon(code: string) {
  if (code === 'm10') return <span className="cashier-method-mark cashier-mark-m10" aria-label="m10">m10</span>;
  if (code === 'terminal') return <span className="cashier-method-mark cashier-mark-terminal" aria-label="Terminal"><svg viewBox="0 0 64 64" aria-hidden="true"><rect x="18" y="7" width="28" height="50" rx="6" fill="none" stroke="currentColor" strokeWidth="4"/><path d="M24 17h16v13H24z" fill="currentColor" opacity=".35"/><g fill="currentColor"><circle cx="26" cy="38" r="2"/><circle cx="33" cy="38" r="2"/><circle cx="40" cy="38" r="2"/><circle cx="26" cy="45" r="2"/><circle cx="33" cy="45" r="2"/><circle cx="40" cy="45" r="2"/></g></svg></span>;
  if (code === 'crypto') return <span className="cashier-method-mark cashier-mark-crypto" aria-label="Kriptovalyuta">₮</span>;
  return <span className="cashier-method-mark cashier-mark-bank" aria-label="Bank"><svg viewBox="0 0 72 72" aria-hidden="true"><path d="M16 14h44L38 35l-14 22-8-10 18-24-18 1z" fill="#fff"/><path d="m37 34 23-20-1 15-18 25H26z" fill="#f3d2da"/></svg></span>;
}

// Bank identity images are remote public brand artwork. A branded text fallback is shown when offline.
// Do not use bank brand assets to imply any bank partnership.
const BANK_LOGOS: Record<string, string> = {
  'Kapital Bank': 'https://commons.wikimedia.org/wiki/Special:FilePath/Kapital_Bank_logo_2025.png',
  'Leobank': 'https://commons.wikimedia.org/wiki/Special:FilePath/Leobank_logo.png',
  'ABB': 'https://commons.wikimedia.org/wiki/Special:FilePath/ABB_Logo.png',
  'Unibank': 'https://commons.wikimedia.org/wiki/Special:FilePath/Unibank_Logo.png',
};
function BankIdentity({ name }: { name: string }) {
  const key = name.toLowerCase();
  const kind = key.includes('kapital') ? 'kapital' : key.includes('leo') ? 'leo' : key.includes('xalq') ? 'xalq' : key.includes('abb') ? 'abb' : key.includes('unibank') ? 'unibank' : key.includes('rabit') ? 'rabita' : key.includes('respublika') ? 'respublika' : key.includes('paşa') ? 'pasha' : 'bank';
  return <span className={`cashier-bank-identity cashier-bank-${kind}`}>
    <span className="cashier-bank-emblem" aria-hidden="true">{BANK_LOGOS[name]
      ? <img loading="lazy" src={BANK_LOGOS[name]} alt="" onError={event => { event.currentTarget.style.display='none'; event.currentTarget.parentElement?.classList.add('logo-unavailable'); }}/>
      : null}<span className="cashier-bank-fallback">{kind === 'kapital' ? 'K' : kind === 'leo' ? '●' : kind === 'xalq' ? 'X' : kind === 'abb' ? 'ABB' : kind === 'pasha' ? 'PAŞA' : name.slice(0, 2).toUpperCase()}</span></span>
    <span className="cashier-bank-wordmark">{name}</span>
  </span>;
}

/** A single bank chooser. Options have an icon, unlike native <select> elements. */
function BankSelector({ value, onChange, label }: { value: string; onChange: (value: string) => void; label: string }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('pointerdown', onPointerDown); document.removeEventListener('keydown', onKeyDown); };
  }, [open]);
  return <div className="cashier-bank-picker cashier-bank-select-only" ref={root}>
    <span className="cashier-bank-label">{label}</span>
    <button type="button" className="cashier-bank-trigger" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)}>
      <BankIdentity name={value}/><span className="cashier-bank-chevron" aria-hidden="true">⌄</span>
    </button>
    {open ? <div className="cashier-bank-menu" role="listbox" aria-label={label}>
      {WALLET_BANKS.map(name => <button key={name} type="button" className={`cashier-bank-menu-item${name === value ? ' selected' : ''}`} role="option" aria-selected={name === value} onClick={() => { onChange(name); setOpen(false); }}><BankIdentity name={name}/>{name === value ? <span className="cashier-bank-check" aria-hidden="true">✓</span> : null}</button>)}
    </div> : null}
  </div>;
}

const QUICK_AMOUNTS_MAJOR = [20, 30, 40, 80, 100, 300];
const WALLET_BANKS = ['Kapital Bank', 'Leobank', 'Xalq Bank', 'ABB', 'Unibank', 'Rabitəbank', 'Bank Respublika', 'PAŞA Bank', 'Digər bank'];
const BANK_CODES = new Set(['card_to_card', 'm10', 'terminal', 'bank_card']);

function isTab(value: string | null): value is CashierTab {
  return value === 'deposit' || value === 'withdraw' || value === 'history';
}

function uniqueAssets(pairs: CryptoPairInfo[]): string[] {
  return [...new Set(pairs.map((p) => p.asset))];
}

function networksFor(pairs: CryptoPairInfo[], asset: string): string[] {
  return pairs.filter((p) => p.asset === asset).map((p) => p.network);
}

function formatClock(totalSeconds: number): string {
  const sec = Math.max(0, totalSeconds);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim() || value;
}

function formatDetail(key: string, value: string): string {
  if (key === 'card_number') return formatCardNumber(value);
  return value;
}

function remainingSeconds(deposit: DepositResponse | null, now: number): number | null {
  if (!deposit || deposit.seconds_remaining == null) return null;
  const origin = Date.parse(deposit.server_time || '') || now;
  return Math.max(0, deposit.seconds_remaining - Math.floor((now - origin) / 1000));
}

export function CashierPage() {
  const { t, i18n } = useTranslation(['payments', 'errors', 'common']);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tabFromUrl = params.get('tab');
  const [tab, setTab] = useState<CashierTab>(isTab(tabFromUrl) ? tabFromUrl : 'deposit');
  const [detailOpen, setDetailOpen] = useState(false);
  const [previewStep, setPreviewStep] = useState<'amount' | 'payment' | 'receipt'>('amount');
  const [withdrawBankName, setWithdrawBankName] = useState('Kapital Bank');
  const [receiptSource, setReceiptSource] = useState<'camera' | 'gallery' | 'file' | null>(null);
  const [bankPreviewName, setBankPreviewName] = useState('Kapital Bank');
  const [methods, setMethods] = useState<{
    deposits: PaymentMethodInfo[];
    withdrawals: PaymentMethodInfo[];
    message: string | null;
    currency: string;
    deposit_expiry_minutes: number;
    usd_azn_rate?: string;
    crypto_min_usd_cents?: number;
    ton_connect_enabled?: boolean;
  }>({
    deposits: [],
    withdrawals: [],
    message: null,
    currency: 'AZN',
    deposit_expiry_minutes: 10,
    ton_connect_enabled: false,
  });
  const [tonConnectEnabled, setTonConnectEnabled] = useState(false);
  const [tonConnectJettonReady, setTonConnectJettonReady] = useState(false);
  const openCryptoKeyRef = useRef<Record<string, string>>({});
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [playOptions, setPlayOptions] = useState<PlayOptions | null>(null);
  const [regionBusy, setRegionBusy] = useState(false);
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [historyMessage, setHistoryMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDeposit, setSelectedDeposit] = useState<string>('card_to_card');
  const [selectedWithdraw, setSelectedWithdraw] = useState<string>('bank_card');
  const [amountMajor, setAmountMajor] = useState('50');
  const [activeDeposit, setActiveDepositReq] = useState<DepositResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [withdrawDest, setWithdrawDest] = useState('');
  const [withdrawHolder, setWithdrawHolder] = useState('');
  const [withdrawExpiry, setWithdrawExpiry] = useState('');
  const [cryptoAsset, setCryptoAsset] = useState('USDT');
  const [cryptoNetwork, setCryptoNetwork] = useState('TRC20');
  const [wdAsset, setWdAsset] = useState('USDT');
  const [wdNetwork, setWdNetwork] = useState('TRC20');
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [depositHistory, setDepositHistory] = useState<DepositResponse[]>([]);
  const [withdrawHistory, setWithdrawHistory] = useState<WithdrawResponse[]>([]);
  const pendingIdempotency = useRef<string | null>(null);

  useEffect(() => {
    if (isTab(tabFromUrl) && tabFromUrl !== tab) setTab(tabFromUrl);
  }, [tabFromUrl, tab]);

  function selectTab(next: CashierTab) {
    setTab(next);
    setDetailOpen(false);
    setPreviewStep('amount');
    setActionMessage(null);
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      return p;
    });
  }

  async function reload() {
    const [methodsRes, walletRes, txRes, depositsRes, withdrawalsRes, tonStatus, optionsRes] = await Promise.all([
      fetchPaymentMethods(),
      fetchWalletSnapshot(),
      fetchTransactions(),
      user ? fetchMyDeposits().catch(() => []) : Promise.resolve([]),
      user ? fetchMyWithdrawals().catch(() => []) : Promise.resolve([]),
      fetchTonConnectStatus().catch(() => ({ enabled: false, asset: '', jetton_transfer_ready: false })),
      fetchPlayOptions().catch(() => null),
    ]);
    if (optionsRes) setPlayOptions(optionsRes);
    const gramTonConnect = Boolean(tonStatus.enabled && tonStatus.asset === 'GRAM');
    const jettonReady = Boolean(gramTonConnect && tonStatus.jetton_transfer_ready !== false);
    setTonConnectEnabled(gramTonConnect);
    setTonConnectJettonReady(jettonReady);
    setMethods({
      deposits: methodsRes.deposits,
      withdrawals: methodsRes.withdrawals,
      message: methodsRes.message,
      currency: methodsRes.currency,
      deposit_expiry_minutes: methodsRes.deposit_expiry_minutes ?? 10,
      usd_azn_rate: methodsRes.usd_azn_rate,
      crypto_min_usd_cents: methodsRes.crypto_min_usd_cents,
      ton_connect_enabled: gramTonConnect,
    });
    setWallet(walletRes);
    setTransactions(txRes.items);
    setHistoryMessage(txRes.message);
    setDepositHistory(depositsRes);
    setWithdrawHistory(withdrawalsRes);
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        await reload();
        if (user) {
          const open = await fetchActiveDeposit();
          if (!cancelled) setActiveDepositReq(open);
        }
      } catch {
        if (!cancelled) setError('network');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [i18n.language, user?.public_id]);

  useEffect(() => {
    if (!activeDeposit || activeDeposit.seconds_remaining == null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [activeDeposit?.public_id, activeDeposit?.server_time, activeDeposit?.seconds_remaining]);

  useEffect(() => {
    if (!user || !activeDeposit) return;
    const id = window.setInterval(() => {
      void fetchActiveDeposit()
        .then((row) => setActiveDepositReq(row))
        .catch(() => undefined);
    }, 12000);
    return () => window.clearInterval(id);
  }, [user, activeDeposit?.public_id]);

  useEffect(() => {
    const address = activeDeposit?.requisite?.details?.address;
    if (!activeDeposit || activeDeposit.method_code !== 'crypto' || !address) {
      setQrUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }
    let revoked = false;
    let created: string | null = null;
    void fetchDepositQr(activeDeposit.public_id)
      .then((url) => {
        created = url;
        if (revoked) URL.revokeObjectURL(url);
        else setQrUrl(url);
      })
      .catch(() => {
        if (!revoked) setQrUrl(null);
      });
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [activeDeposit?.public_id, activeDeposit?.crypto_network, activeDeposit?.status, activeDeposit?.requisite?.details?.address]);

  const depositMethod = useMemo(
    () => methods.deposits.find((item) => item.code === selectedDeposit) || null,
    [methods.deposits, selectedDeposit],
  );
  const withdrawMethod = useMemo(
    () => methods.withdrawals.find((item) => item.code === selectedWithdraw) || null,
    [methods.withdrawals, selectedWithdraw],
  );

  const depositPairs = methods.deposits.find((item) => item.code === 'crypto')?.crypto_pairs ?? [];
  const withdrawPairs = methods.withdrawals.find((item) => item.code === 'crypto')?.crypto_pairs ?? [];
  const depositPairsEffective = useMemo(() => {
    const base = (depositPairs.length ? [...depositPairs] : CRYPTO_SHOWCASE.map(([asset, network]) => ({ asset, network } as CryptoPairInfo)))
      .filter((p) => !isHiddenCryptoPair(p.asset, p.network))
      .filter((p) => CRYPTO_PAIR_PRIORITY.some(([a, n]) => a === p.asset && n === p.network));
    return sortCryptoPairs(base);
  }, [depositPairs]);
  const depositAssets = uniqueAssets(depositPairsEffective);
  const depositNets = networksFor(depositPairsEffective, cryptoAsset);
  void depositAssets; void depositNets;
  const isTonConnectPair = tonConnectEnabled && tonConnectJettonReady && cryptoAsset === 'GRAM' && cryptoNetwork === 'TON';
  const activeIsTonConnect = Boolean(activeDeposit?.ton_connect);
  const withdrawAssets = uniqueAssets(withdrawPairs.length ? withdrawPairs : CRYPTO_SHOWCASE.map(([asset, network]) => ({ asset, network } as CryptoPairInfo)));
  const withdrawNets = networksFor(withdrawPairs.length ? withdrawPairs : CRYPTO_SHOWCASE.map(([asset, network]) => ({ asset, network } as CryptoPairInfo)), wdAsset);

  useEffect(() => {
    if (selectedDeposit !== 'crypto') return;
    const preferred = depositPairsEffective[0];
    if (!preferred) return;
    const ok = depositPairsEffective.some((p) => p.asset === cryptoAsset && p.network === cryptoNetwork);
    if (!ok) {
      setCryptoAsset(preferred.asset);
      setCryptoNetwork(preferred.network);
    }
  }, [selectedDeposit, depositPairsEffective, cryptoAsset, cryptoNetwork]);
  useEffect(() => {
    if (selectedWithdraw !== 'crypto') return;
    if (withdrawAssets.length && !withdrawAssets.includes(wdAsset)) setWdAsset(withdrawAssets[0]);
  }, [selectedWithdraw, withdrawAssets, wdAsset]);
  useEffect(() => {
    if (selectedWithdraw !== 'crypto') return;
    if (withdrawNets.length && !withdrawNets.includes(wdNetwork)) setWdNetwork(withdrawNets[0]);
  }, [selectedWithdraw, withdrawNets, wdNetwork]);

  const fallbackBankMethods = ['card_to_card', 'm10', 'terminal'].map((code) => ({ code, enabled: false, crypto_pairs: [] } as unknown as PaymentMethodInfo));
  const fallbackWithdrawalMethods = ['bank_card', 'm10'].map((code) => ({ code, enabled: false, crypto_pairs: [] } as unknown as PaymentMethodInfo));
  const fallbackCryptoMethods = [{ code: 'crypto', enabled: false, crypto_pairs: [] } as unknown as PaymentMethodInfo];
  const paymentRegion = (wallet?.payment_region || playOptions?.preferences?.payment_region || 'AZ').toUpperCase();
  const bankAllowed = playOptions?.preferences?.bank_methods_allowed ?? paymentRegion === 'AZ';
  const depositBankRaw = methods.deposits.filter((m) => BANK_CODES.has(m.code));
  const depositBank = bankAllowed
    ? (depositBankRaw.length ? depositBankRaw : fallbackBankMethods)
    : [];
  const depositCrypto = methods.deposits.filter((m) => m.code === 'crypto').length ? methods.deposits.filter((m) => m.code === 'crypto') : fallbackCryptoMethods;
  const withdrawBankRaw = methods.withdrawals.filter((m) => BANK_CODES.has(m.code));
  const withdrawBank = bankAllowed
    ? (withdrawBankRaw.length ? withdrawBankRaw : fallbackWithdrawalMethods)
    : [];
  const withdrawCrypto = methods.withdrawals.filter((m) => m.code === 'crypto').length ? methods.withdrawals.filter((m) => m.code === 'crypto') : fallbackCryptoMethods;

  async function onRegionChange(code: string) {
    if (!user || regionBusy || code === paymentRegion) return;
    setRegionBusy(true);
    setActionMessage(null);
    setAmountMajor('');
    try {
      markPaymentRegionManual();
      await savePreferences({ payment_region: code });
      await reload();
    } catch (err) {
      setActionMessage(paymentErrorMessage(err, t('errors:generic')));
    } finally {
      setRegionBusy(false);
    }
  }

  // Keep cashier region aligned with play currency unless the user chose a region manually.
  useEffect(() => {
    if (!user || regionBusy || loading) return;
    if (hasManualPaymentRegion()) return;
    const play = (wallet?.play_currency || playOptions?.preferences?.play_currency || '').toUpperCase();
    const expected = suggestedRegionForCurrency(play);
    if (!expected || expected === paymentRegion) return;
    let cancelled = false;
    setRegionBusy(true);
    void savePreferences({ payment_region: expected })
      .then(() => {
        if (!cancelled) return reload();
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setRegionBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.public_id, wallet?.play_currency, playOptions?.preferences?.play_currency, paymentRegion, loading]);

  function renderRegionPicker() {
    const allowed = new Set(['AZ', 'RU', 'US', 'EU']);
    const defaults: PaymentRegionOption[] = [
      { code: 'AZ', name: '🇦🇿 Azərbaycan' },
      { code: 'RU', name: '🇷🇺 Rusiya' },
      { code: 'US', name: '🇺🇸 ABŞ' },
      { code: 'EU', name: '🇪🇺 Avro zonası' },
    ];
    const fromApi = (playOptions?.payment_regions || []).filter((row) => allowed.has(row.code.toUpperCase()));
    const byCode = new Map(defaults.map((row) => [row.code, row]));
    for (const row of fromApi) {
      const code = row.code.toUpperCase();
      byCode.set(code, { code, name: defaults.find((d) => d.code === code)?.name || row.name });
    }
    // Keep a legacy stored region visible until the user switches (do not delete history).
    if (paymentRegion && !byCode.has(paymentRegion)) {
      byCode.set(paymentRegion, { code: paymentRegion, name: paymentRegion });
    }
    const regions = Array.from(byCode.values());
    return (
      <div className="cashier-region cashier-region-select">
        <span>{t('payments:regionLabel')}</span>
        <select
          aria-label={t('payments:regionLabel')}
          value={paymentRegion}
          disabled={!user || regionBusy}
          onChange={(e) => void onRegionChange(e.target.value)}
        >
          {regions.map((row) => (
            <option key={row.code} value={row.code}>
              {row.name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  const locale = i18n.language || 'az';
  const displayCurrency = (REGION_DISPLAY_CURRENCY[paymentRegion] || 'AZN') as PlayCurrency;
  const displayRateRaw = playOptions?.fx?.rates_azn_per_unit?.[displayCurrency];
  const displayRateNum = Number(displayRateRaw);
  const fxReady =
    displayCurrency === 'AZN' ||
    (Boolean(displayRateRaw) && Number.isFinite(displayRateNum) && displayRateNum > 0 && !playOptions?.fx?.stale);
  const displayRate = fxReady ? (displayCurrency === 'AZN' ? 1 : displayRateRaw) : undefined;
  const amountMajorNum = Number.parseFloat(amountMajor.replace(',', '.')) || 0;
  // Ledger / API amounts stay AZN minor; UI amount is region display currency.
  const amountMinor = fxReady ? playMajorToAznMinor(amountMajorNum, displayRate) : 0;
  const amountDisplayMinor = Math.round(amountMajorNum * 100);
  const currencySymbol = CURRENCY_SYMBOL[displayCurrency] || displayCurrency;
  const clock = remainingSeconds(activeDeposit, now);
  const openDeposit = Boolean(activeDeposit && (activeDeposit.status === 'awaiting_receipt' || activeDeposit.status === 'created'));
  // Crypto open deposits must NEVER block card/m10/terminal amount + continue flow.
  const activeBankDeposit = activeDeposit && activeDeposit.method_code !== 'crypto' ? activeDeposit : null;
  const openBankDeposit = Boolean(
    activeBankDeposit && (activeBankDeposit.status === 'awaiting_receipt' || activeBankDeposit.status === 'created'),
  );
  const bankRequisiteOpen = Boolean(openBankDeposit && activeBankDeposit);

  function formatLimitMinor(aznMinor: number, mode: 'min' | 'max'): string {
    if (!fxReady || displayRate == null) return '—';
    const converted =
      mode === 'min'
        ? aznMinorToPlayMinorCeil(aznMinor, displayRate)
        : aznMinorToPlayMinorFloor(aznMinor, displayRate);
    return formatMinor(converted, displayCurrency, locale);
  }
  const cryptoReady = !CASHIER_PREVIEW_MODE && selectedDeposit === 'crypto' && Boolean(depositMethod?.enabled || isTonConnectPair);
  const cryptoInstantMode = selectedDeposit === 'crypto' && (Boolean(depositMethod?.enabled) || isTonConnectPair || CASHIER_PREVIEW_MODE);
  const cryptoPayOpen = Boolean(detailOpen && cryptoInstantMode && tab === 'deposit');

  // Leaving crypto for bank rails: release cancellable crypto hold so amount form works.
  useEffect(() => {
    if (!detailOpen || selectedDeposit === 'crypto' || CASHIER_PREVIEW_MODE || !user) return;
    if (!activeDeposit || activeDeposit.method_code !== 'crypto' || !activeDeposit.can_cancel) return;
    let cancelled = false;
    void cancelDeposit(activeDeposit.public_id)
      .then(() => {
        if (cancelled) return;
        setActiveDepositReq(null);
        void reload();
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [detailOpen, selectedDeposit, activeDeposit?.public_id, activeDeposit?.method_code, activeDeposit?.can_cancel, user]);

  // Amount-less crypto: open a shared-address deposit as soon as the pair is selected.
  useEffect(() => {
    if (!detailOpen || selectedDeposit !== 'crypto') return;
    if (!user || !cryptoReady || !cryptoAsset || !cryptoNetwork || CASHIER_PREVIEW_MODE) return;
    if (activeDeposit?.method_code === 'crypto'
      && activeDeposit.crypto_asset === cryptoAsset
      && activeDeposit.crypto_network === cryptoNetwork) {
      return;
    }
    if (activeDeposit && !(activeDeposit.method_code === 'crypto'
      && activeDeposit.crypto_asset === cryptoAsset
      && activeDeposit.crypto_network === cryptoNetwork)) {
      return;
    }
    let cancelled = false;
    const pairKey = `${cryptoAsset}:${cryptoNetwork}`;
    const key = openCryptoKeyRef.current[pairKey] || newIdempotencyKey('copen');
    openCryptoKeyRef.current[pairKey] = key;
    void createOpenCryptoDeposit({ asset: cryptoAsset, network: cryptoNetwork, idempotency_key: key })
      .then((row) => {
        if (cancelled) return;
        setActiveDepositReq(row);
        setActionMessage(null);
      })
      .catch(async (err) => {
        if (cancelled) return;
        const code = paymentErrorMessage(err, '');
        if (code === 'active_deposit_exists') {
          const open = await fetchActiveDeposit().catch(() => null);
          if (open) setActiveDepositReq(open);
        }
        setActionMessage(t(`payments:err_${code}`, { defaultValue: code || t('errors:generic') }));
      });
    return () => {
      cancelled = true;
    };
  }, [detailOpen, selectedDeposit, user, cryptoReady, cryptoAsset, cryptoNetwork, activeDeposit?.public_id, activeDeposit?.crypto_asset, activeDeposit?.crypto_network, t]);

  async function onDeposit() {
    if (CASHIER_PREVIEW_MODE) {
      if (amountMinor <= 0 || !Number.isFinite(amountMinor)) { setActionMessage(t('payments:invalidAmount')); return; }
      setPreviewStep('payment');
      setActionMessage(t('payments:previewDepositSelected', { method: selectedDeposit === 'crypto' ? `${cryptoAsset} (${cryptoNetwork})` : selectedDeposit, amount: amountMajor }));
      return;
    }
    if (!fxReady) {
      setActionMessage(t('payments:fxUnavailable', { defaultValue: 'Valyuta məzənnəsi əlçatan deyil' }));
      return;
    }
    if (!user) return;
    if (!depositMethod?.enabled && !isTonConnectPair) {
      setActionMessage(
        selectedDeposit === 'crypto' ? t('payments:cryptoUnavailable') : t('payments:methodDisabled'),
      );
      return;
    }
    if (selectedDeposit === 'crypto' && (!cryptoAsset || !cryptoNetwork)) {
      setActionMessage(t('payments:noCryptoNetworks'));
      return;
    }
    setBusy(true);
    setActionMessage(null);
    const key = pendingIdempotency.current || newIdempotencyKey('dep');
    pendingIdempotency.current = key;
    try {
      // Bank rails must not be blocked by a leftover amount-open crypto deposit.
      if (
        selectedDeposit !== 'crypto'
        && activeDeposit?.method_code === 'crypto'
        && activeDeposit.can_cancel
      ) {
        await cancelDeposit(activeDeposit.public_id);
        setActiveDepositReq(null);
      }
      const created = isTonConnectPair
        ? await createTonConnectDeposit({ amount_minor: amountMinor, idempotency_key: key })
        : await createDeposit({
            method_code: selectedDeposit,
            amount_minor: amountMinor,
            idempotency_key: key,
            asset: selectedDeposit === 'crypto' ? cryptoAsset : undefined,
            network: selectedDeposit === 'crypto' ? cryptoNetwork : undefined,
          });
      pendingIdempotency.current = null;
      setActiveDepositReq(created);
      setNow(Date.now());
      setActionMessage(
        isTonConnectPair
          ? (created.message || t('payments:tonDepositCreated'))
          : selectedDeposit === 'crypto'
            ? (created.message || t('payments:depositCreated'))
            : null,
      );
      await reload();
    } catch (err) {
      const code = paymentErrorMessage(err, t('errors:generic'));
      if (code === 'active_deposit_exists') {
        const open = await fetchActiveDeposit().catch(() => null);
        if (open) setActiveDepositReq(open);
        pendingIdempotency.current = null;
      }
      setActionMessage(t(`payments:err_${code}`, { defaultValue: code }));
    } finally {
      setBusy(false);
    }
  }

  async function onReceipt(file: File | null) {
    if (!file || !activeDeposit) return;
    setBusy(true);
    try {
      const updated = await uploadDepositReceipt(activeDeposit.public_id, file);
      setActiveDepositReq(updated);
      setActionMessage(t('payments:receiptUploaded'));
      await reload();
    } catch (err) {
      setActionMessage(paymentErrorMessage(err, t('errors:generic')));
    } finally {
      setBusy(false);
    }
  }

  async function onCancel() {
    if (!activeDeposit) return;
    setBusy(true);
    try {
      await cancelDeposit(activeDeposit.public_id);
      setActiveDepositReq(null);
      setActionMessage(t('payments:depositCancelled'));
      await reload();
    } catch (err) {
      setActionMessage(paymentErrorMessage(err, t('errors:generic')));
    } finally {
      setBusy(false);
    }
  }

  async function onWithdraw() {
    if (CASHIER_PREVIEW_MODE) {
      if (amountMinor <= 0 || !Number.isFinite(amountMinor)) { setActionMessage(t('payments:invalidAmount')); return; }
      setActionMessage(t('payments:previewWithdrawChecked', { method: selectedWithdraw === 'crypto' ? `${wdAsset} (${wdNetwork})` : selectedWithdraw, amount: amountMajor }));
      return;
    }
    if (!fxReady) {
      setActionMessage(t('payments:fxUnavailable', { defaultValue: 'Valyuta məzənnəsi əlçatan deyil' }));
      return;
    }
    if (!user) return;
    if (!withdrawMethod?.enabled) {
      setActionMessage(t('payments:methodDisabled'));
      return;
    }
    if (!withdrawDest.trim()) {
      setActionMessage(t('payments:destinationRequired'));
      return;
    }
    setBusy(true);
    setActionMessage(null);
    try {
      const destination: Record<string, string> =
        selectedWithdraw === 'm10'
          ? { m10_account: withdrawDest.trim() }
          : selectedWithdraw === 'crypto'
            ? { asset: wdAsset, network: wdNetwork, address: withdrawDest.trim() }
            : { card_number: withdrawDest.trim(), ...(withdrawHolder.trim() ? { card_holder: withdrawHolder.trim() } : {}) };
      await createWithdrawal({
        method_code: selectedWithdraw,
        amount_minor: amountMinor,
        destination,
        idempotency_key: newIdempotencyKey('wd'),
      });
      setActionMessage(t('payments:withdrawAcceptedClean'));
      setWithdrawDest('');
      await reload();
    } catch (err) {
      setActionMessage(paymentErrorMessage(err, t('errors:generic')));
    } finally {
      setBusy(false);
    }
  }

  function renderMethodGroup(
    title: string,
    items: PaymentMethodInfo[],
    selected: string,
    onSelect: (code: string) => void,
    labels: Record<string, string>,
  ) {
    if (items.length === 0) return null;
    const isCryptoGroup = items.some((item) => item.code === 'crypto');
    return (
      <section className="method-group" aria-label={title}>
        <h3>{title}</h3>
        {isCryptoGroup ? (
          <div className="cashier-crypto-grid">
            {(tab === 'deposit'
              ? depositPairsEffective.map((pair) => ({ asset: pair.asset, network: pair.network }))
              : withdrawPairs.length
                ? withdrawPairs.map((pair) => ({ asset: pair.asset, network: pair.network }))
                : CRYPTO_SHOWCASE.map(([asset, network]) => ({ asset, network }))
            ).map((pair, index) => {
              if (tab === 'deposit' && isHiddenCryptoPair(pair.asset, pair.network)) return null;
              if (tab === 'deposit' && !CRYPTO_PAIR_PRIORITY.some(([a, n]) => a === pair.asset && n === pair.network)) return null;
              const tonConnectAvailable = tonConnectEnabled && tonConnectJettonReady && pair.asset === 'GRAM' && pair.network === 'TON';
              const available = CASHIER_PREVIEW_MODE || tonConnectAvailable || (items.some((item) => item.enabled) &&
                (tab === 'deposit' ? depositPairs : withdrawPairs)
                  .some((configured) => configured.asset === pair.asset && configured.network === pair.network));
              const active = selected === 'crypto' &&
                (tab === 'deposit' ? cryptoAsset === pair.asset && cryptoNetwork === pair.network
                  : wdAsset === pair.asset && wdNetwork === pair.network);
              return (
                <button
                  key={`${pair.asset}-${pair.network}-${index}`}
                  type="button"
                  className={`cashier-crypto-tile${active ? ' active' : ''}${available ? '' : ' unavailable'}`}
                  onClick={() => {
                    if (!available) return;
                    onSelect('crypto');
                    setDetailOpen(true); setPreviewStep('amount'); setActionMessage(null);
                    if (tab === 'deposit') { setCryptoAsset(pair.asset); setCryptoNetwork(pair.network); }
                    else { setWdAsset(pair.asset); setWdNetwork(pair.network); }
                  }}
                  aria-pressed={active}
                  aria-disabled={!available}
                >
                  <span className="cashier-crypto-name">{pair.asset} <small>({pair.network})</small></span>
                  {tab === 'deposit' && available ? (
                    <span className="cashier-crypto-status cashier-crypto-bonus">
                      <svg className="cashier-bonus-gift" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 10h16v10H4zM3 7h18v4H3zM12 7v13M12 7C9 7 7 6 7 4.5 7 3.1 8.1 2 9.5 2 11.2 2 12 4.5 12 7Zm0 0c3 0 5-1 5-2.5C17 3.1 15.9 2 14.5 2 12.8 2 12 4.5 12 7Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/></svg>
                      <span>+5%</span>
                    </span>
                  ) : !available ? (
                    <span className="cashier-crypto-status cashier-crypto-unavailable">{t('payments:methodDisabled')}</span>
                  ) : null}
                  {coinIcon(pair.asset)}
                  <span className={`cashier-network-badge cashier-network-${pair.network.toLowerCase()}`} aria-label={`${t('payments:network')}: ${pair.network}`}>
                    <img src={`/cashier-icons/network-${pair.network.toLowerCase()}.svg`} alt="" loading="lazy" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="method-grid">
            {items.map((method) => {
              const labelKey = labels[method.code] || method.code;
              return (
                <button key={method.code} type="button"
                  className={`method-tile${selected === method.code ? ' active' : ''}${method.enabled || CASHIER_PREVIEW_MODE ? '' : ' disabled'}`}
                  onClick={() => { onSelect(method.code); setDetailOpen(true); setPreviewStep('amount'); setActionMessage(null); }} aria-pressed={selected === method.code}>
                  <strong>{t(`payments:${labelKey}`)}</strong>
                  {methodIcon(method.code)}
                  <small>{CASHIER_PREVIEW_MODE ? t('payments:tapToSelect') : method.enabled
                    ? t(`payments:${labelKey}Desc`, { defaultValue: '' })
                    : t('payments:methodDisabled')}</small>
                </button>
              );
            })}
          </div>
        )}
      </section>
    );
  }

  function pairChips(assets: string[], networks: string[], asset: string, network: string, onAsset: (v: string) => void, onNetwork: (v: string) => void) {
    if (!assets.length) return <p className="muted-note">{t('payments:noPairsConfigured')}</p>;
    return (
      <div className="pair-picker">
        <p className="pair-label">{t('payments:selectCoin')}</p>
        <div className="chips">
          {assets.map((code) => (
            <button key={code} type="button" className={`chip${asset === code ? ' active' : ''}`} onClick={() => onAsset(code)}>
              {code}
            </button>
          ))}
        </div>
        <p className="pair-label">{t('payments:selectNetwork')}</p>
        <div className="chips">
          {networks.map((code) => (
            <button key={code} type="button" className={`chip${network === code ? ' active' : ''}`} onClick={() => onNetwork(code)}>
              {code}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`page cashier-page${bankRequisiteOpen ? " cashier-requisite-mode" : ""}${cryptoPayOpen ? " cashier-crypto-pay-mode" : ""}`}>
      <div className="cashier-shell cashier-sheet">
        <div className="cashier-head">
          <div>
            <h1>{t('payments:openCashier')}</h1>
            
          </div>
          <div className="cashier-head-actions">
            <div className="cashier-balance-chip">
              <span>{wallet?.play_currency || methods.currency}</span>
              <strong>{formatMinor(wallet?.play_balance_real_minor ?? wallet?.balance_real_minor ?? null, wallet?.play_currency || methods.currency, locale)}</strong>
            </div>
            {!cryptoPayOpen ? (
              <button type="button" className="icon-btn" aria-label={t('common:close')} onClick={() => navigate(-1)}>
                ×
              </button>
            ) : null}
          </div>
        </div>

        {!cryptoPayOpen ? (
        <div className="cashier-tabs" role="tablist" aria-label={t('payments:walletSectionsLocal')}>
          {(
            [
              ['deposit', 'depositTab'],
              ['withdraw', 'withdrawTab'],
              ['history', 'historyTab'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`cashier-tab${tab === id ? ' active' : ''}`}
              onClick={() => selectTab(id)}
            >
              {t(`payments:${label}`)}
            </button>
          ))}
        </div>
        ) : null}

        {loading ? (
          <div className="skeleton" style={{ minHeight: 280 }} aria-busy="true" />
        ) : error ? (
          <div className="state-card" role="alert">
            <h3>{t('errors:generic')}</h3>
            <p>{error}</p>
          </div>
        ) : tab === 'history' ? (
          <HistoryPanel items={transactions} deposits={depositHistory} withdrawals={withdrawHistory} message={historyMessage} />
        ) : tab === 'deposit' ? (
          <div className="cashier-panel">
            {detailOpen && !bankRequisiteOpen ? (
              <button
                type="button"
                className="cashier-back"
                onClick={() => {
                  // Back to method list only — never auto-cancel pending crypto/admin review.
                  if (CASHIER_PREVIEW_MODE && selectedDeposit !== 'crypto' && previewStep !== 'amount') {
                    setPreviewStep(previewStep === 'receipt' ? 'payment' : 'amount');
                  } else {
                    setDetailOpen(false);
                  }
                  setActionMessage(null);
                }}
              >
                ‹ &nbsp;{' '}
                {cryptoPayOpen
                  ? t('payments:cryptoBack')
                  : CASHIER_PREVIEW_MODE && previewStep !== 'amount'
                    ? t('payments:prevStep')
                    : t('payments:paymentMethodsLocal')}
              </button>
            ) : null}
            {!detailOpen ? <>{renderRegionPicker()}
            {CASHIER_PREVIEW_MODE ? <p className="muted-note" role="status">{t('payments:previewBanner')}</p> : methods.message ? <p className="muted-note">{methods.message}</p> : null}
            {renderMethodGroup(t('payments:bankPayment', { defaultValue: 'Bank payment' }), depositBank, selectedDeposit, setSelectedDeposit, DEPOSIT_LABELS)}
            {renderMethodGroup(t('payments:crypto'), depositCrypto, selectedDeposit, setSelectedDeposit, DEPOSIT_LABELS)}
            </> : bankRequisiteOpen || cryptoPayOpen ? null : <div className="cashier-detail-heading"><span className="cashier-step-mark">{selectedDeposit === 'crypto' ? coinIcon(cryptoAsset) : methodIcon(selectedDeposit)}</span><div><h2>{selectedDeposit === 'crypto' ? t('payments:cryptoDepositTitle') : t(`payments:${DEPOSIT_LABELS[selectedDeposit] || selectedDeposit}`)}</h2><small>{selectedDeposit === 'crypto' ? `${cryptoAsset} (${cryptoNetwork})` : previewStep === 'amount' ? t('payments:depositAmount') : previewStep === 'payment' ? t('payments:paymentInstruction') : t('payments:paymentConfirm')}</small></div></div>}
            {detailOpen && CASHIER_PREVIEW_MODE && selectedDeposit !== 'crypto' ? <div className="cashier-progress" aria-label={t('payments:depositStages')}>{[t('payments:stageAmount'), t('payments:stagePayment'), t('payments:stageReceipt')].map((text,index) => <div key={text} className={`cashier-progress-item${index <= (previewStep === 'amount' ? 0 : previewStep === 'payment' ? 1 : 2) ? ' done' : ''}`}><span>{index+1}</span>{text}</div>)}</div> : null}
            {detailOpen && CASHIER_PREVIEW_MODE && previewStep === 'payment' ? (
              <section className="cashier-demo-instruction" aria-label={t('payments:paymentInstruction')}>
                <div className="cashier-step-indicator">02 / 03 · {t('payments:paymentDetailsStep')}</div><div className="cashier-preview-timer" role="status"><span>◷</span> 10:00 <small>{t('payments:sampleTimer')}</small></div>
                <h3>{selectedDeposit === 'crypto' ? `${cryptoAsset} · ${cryptoNetwork}` : t('payments:transferDetails')}</h3>
                <div className="cashier-requisite-surface">
                  <div><span>{t('payments:paymentAmountLocal')}</span><strong>{formatMinor(amountMinor, methods.currency, locale)}</strong></div>
                  {selectedDeposit === 'crypto' ? <>
                    <div><span>{t('payments:cryptoAsset')}</span><strong>{cryptoAsset}</strong></div>
                    <div><span>{t('payments:network')}</span><strong>{cryptoNetwork}</strong></div>
                    <div><span>{t('payments:receiveAddress')}</span><strong className="cashier-unset">{t('payments:notAssigned')}</strong></div>
                    <div className="cashier-fake-qr" aria-label={t('payments:qrWhenReady')}><span className="cashier-noqr-symbol">▦</span><strong>{t('payments:qrInactive')}</strong><small>{t('payments:qrInactiveHint')}</small></div>
                  </> : <>
                    <div><span>{t('payments:paymentMethod')}</span><strong>{selectedDeposit === 'm10' ? 'm10' : selectedDeposit === 'terminal' ? 'Terminal' : 'Kartdan karta'}</strong></div>
                    <div><span>{t('payments:bank')}</span><strong>{bankPreviewName}</strong></div>
                    <div><span>{t('payments:recipient')}</span><strong className="cashier-unset">{t('payments:notAssigned')}</strong></div>
                    <div><span>{t('payments:cardNumber')}</span><strong className="cashier-unset">{t('payments:notAssigned')}</strong></div>
                  </>}
                </div>
                {selectedDeposit === 'crypto' ? <>
                  <div className="cashier-warning"><strong>{t('payments:networkAttention', { network: cryptoNetwork })}</strong><span>{t('payments:networkAttentionBody')}</span></div>
                  <div className="cashier-warning"><strong>{t('payments:minDepositFees')}</strong><span>{t('payments:minDepositFeesBody')}</span></div>
                </> : <>
                  <div className="cashier-warning"><strong>{t('payments:waitingTransfer')}</strong><span>{t('payments:waitingTransferBody')}</span></div>
                  <div className="cashier-warning"><strong>{t('payments:wrongTransfer')}</strong><span>{t('payments:wrongTransferBody')}</span></div>
                </>}
                <div className="cashier-demo-flag">{t('payments:previewNoAddress')}</div>
                <button type="button" className="btn btn-primary btn-block" onClick={() => { setPreviewStep('receipt'); setActionMessage(null); }}>{t('payments:iPaidReceipt')}</button>
              </section>
            ) : null}
            {detailOpen && CASHIER_PREVIEW_MODE && previewStep === 'receipt' ? (
              <section className="cashier-demo-instruction" aria-label={t('payments:receiptSend')}>
                <div className="cashier-step-indicator">03 / 03 · {t('payments:paymentConfirmStep')}</div>
                <h3>{t('payments:addReceiptTitle')}</h3>
                <p>{t('payments:addReceiptBody')}</p>
                <div className="cashier-receipt-choices">
                  <button type="button" className="cashier-receipt-choice" onClick={() => { setReceiptSource('camera'); document.getElementById('vbb-receipt-camera')?.click(); }}><span aria-hidden="true">📷</span> {t('payments:cameraCapture')}</button>
                  <button type="button" className="cashier-receipt-choice" onClick={() => { setReceiptSource('gallery'); document.getElementById('vbb-receipt-gallery')?.click(); }}><span aria-hidden="true">▧</span> {t('payments:fromGallery')}</button>
                  <button type="button" className="cashier-receipt-choice" onClick={() => { setReceiptSource('file'); document.getElementById('vbb-receipt-file')?.click(); }}><span aria-hidden="true">📄</span> {t('payments:chooseFile')}</button>
                </div>
                <input id="vbb-receipt-camera" type="file" accept="image/*" capture="environment" hidden onChange={e => setActionMessage(e.target.files?.[0] ? t('payments:cameraFileSelected', { name: e.target.files[0].name }) : null)}/>
                <input id="vbb-receipt-gallery" type="file" accept="image/*" hidden onChange={e => setActionMessage(e.target.files?.[0] ? t('payments:imageSelected', { name: e.target.files[0].name }) : null)}/>
                <input id="vbb-receipt-file" type="file" accept="image/*,application/pdf" hidden onChange={e => setActionMessage(e.target.files?.[0] ? t('payments:fileSelected', { name: e.target.files[0].name }) : null)}/>
                <div className="cashier-demo-flag">{t('payments:previewTest')} · {receiptSource ? t('payments:checkingFileSelection') : t('payments:fileNotUploaded')}</div>
              </section>
            ) : null}

            {detailOpen && selectedDeposit === 'crypto' && !depositMethod?.enabled && !isTonConnectPair && !CASHIER_PREVIEW_MODE && !activeDeposit ? (
              <div className="state-card" role="status">
                <h3>{t('payments:cryptoUnavailable')}</h3>
                <p>{t('payments:cryptoUnavailableHint')}</p>
              </div>
            ) : null}

            {detailOpen && selectedDeposit === 'card_to_card' && CASHIER_PREVIEW_MODE && previewStep === 'amount' ? (
              <BankSelector value={bankPreviewName} onChange={setBankPreviewName} label={t('payments:bankSelect')}/>
            ) : null}

            {detailOpen && !activeBankDeposit && selectedDeposit !== 'crypto' && (!CASHIER_PREVIEW_MODE || previewStep === 'amount') ? (
            <div className="amount-block">
              <p className="pair-label">{t(`payments:${DEPOSIT_LABELS[selectedDeposit] || selectedDeposit}`)}</p>
              <label htmlFor="deposit-amount">{t('payments:amount')}</label>
              <div className="chips" aria-label={t('payments:quickAmounts')}>
                {QUICK_AMOUNTS_MAJOR.map((aznMajor) => {
                  const displayMajor = fxReady
                    ? (aznMinorToPlayMinorCeil(aznMajor * 100, displayRate) / 100)
                    : aznMajor;
                  const label = Number.isInteger(displayMajor) ? String(displayMajor) : displayMajor.toFixed(2);
                  return (
                  <button
                    key={aznMajor}
                    type="button"
                    className={`chip${amountMajor === label ? ' active' : ''}`}
                    disabled={!fxReady}
                    onClick={() => setAmountMajor(label)}
                  >
                    {label} {currencySymbol}
                  </button>
                  );
                })}
              </div>
              <input
                id="deposit-amount"
                className="field-input"
                inputMode="decimal"
                value={amountMajor}
                onChange={(event) => setAmountMajor(event.target.value)}
                disabled={openBankDeposit || !fxReady}
              />
              {!fxReady ? (
                <p className="error-note" role="alert">{t('payments:fxUnavailable', { defaultValue: 'Valyuta məzənnəsi əlçatan deyil' })}</p>
              ) : depositMethod?.limits ? (
                <p className="muted-note">
                  {t('payments:minMax', {
                    min: formatLimitMinor(Number(depositMethod.limits.min_minor || 0), 'min'),
                    max: formatLimitMinor(Number(depositMethod.limits.max_minor || 0), 'max'),
                  })}
                </p>
              ) : null}

            </div>
            ) : null}

            {detailOpen && CASHIER_PREVIEW_MODE && selectedDeposit !== 'crypto' && previewStep === 'amount' && amountMinor > 0 ? <div className="cashier-bonus-preview"><span className="cashier-bonus-stamp">✦ 5%</span><div><strong>{t('payments:depositBonusSample')}</strong><small>{t('payments:bonusSampleLine', { bonus: formatMinor(Math.round(amountMinor * .05), methods.currency, locale), total: formatMinor(Math.round(amountMinor * 1.05), methods.currency, locale) })}. Real balansda hesablanmır.</small></div></div> : null}

            {detailOpen && cryptoInstantMode ? (
              <CryptoInstantDepositPanel
                asset={cryptoAsset}
                network={cryptoNetwork}
                deposit={activeDeposit?.method_code === 'crypto' ? activeDeposit : null}
                coinIcon={coinIcon(cryptoAsset)}
                onDepositUpdated={(next) => {
                  setActiveDepositReq(next);
                  void reload();
                }}
              />
            ) : null}

            {detailOpen && activeDeposit && activeDeposit.method_code !== 'crypto' ? (
              <div className={`instruction-card deposit-ticket${bankRequisiteOpen ? ' cashier-requisite-focus' : ''}`}>
                {activeDeposit.method_code !== 'crypto' ? <>
                <div className="ticket-head">
                  <p>
                    <strong>{t('payments:orderNumber')}</strong> {activeDeposit.public_id}
                  </p>
                  <p className="cashier-payment-amount">
                    <strong>{t('payments:amountDue')}</strong>{' '}
                    {formatMinor(activeDeposit.amount_minor, methods.currency, locale)}
                  </p>
                  {clock != null && openDeposit ? (
                    <div className={`countdown${clock === 0 ? ' expired' : ''}`} aria-live="polite">
                      <span>{t('payments:timeRemaining')}</span>
                      <strong>{formatClock(clock)}</strong>
                    </div>
                  ) : null}
                </div>
                <p>
                  <strong>Status:</strong>{' '}
                  {({
                    awaiting_receipt: t('payments:statusPending'),
                    pending: t('payments:statusReview'),
                    pending_review: t('payments:statusReview'),
                    approved: t('payments:statusApproved'),
                    completed: t('payments:statusCompleted'),
                    rejected: t('payments:rejected'),
                    expired: 'Vaxt bitdi',
                    cancelled: t('payments:statusCancelled'),
                  } as Record<string, string>)[activeDeposit.display_status || activeDeposit.status]
                    || t(`payments:status_${activeDeposit.display_status || activeDeposit.status}`, {
                      defaultValue: t('payments:requestProcessing'),
                    })}
                  {activeDeposit.crypto_asset ? ` · ${activeDeposit.crypto_asset}/${activeDeposit.crypto_network}` : ''}
                </p>
                </> : null}
                {clock === 0 || activeDeposit.status === 'expired' || activeDeposit.display_status === 'expired' ? (
                  <>
                    <p className="error-note">{t('payments:depositExpired')}</p>
                    {activeDeposit.late_payment_possible ? <p className="muted-note">{t('payments:expiredLateNote')}</p> : null}
                  </>
                ) : null}
                {activeDeposit.method_code !== 'crypto' ? (
                  <p className="error-note">{t('payments:exactAmountWarn')}</p>
                ) : null}
                {activeDeposit.method_code !== 'crypto' && activeDeposit.crypto_amount ? (
                  <p>
                    {activeDeposit.crypto_amount} {activeDeposit.crypto_asset}

                  </p>
                ) : null}
                {activeDeposit.method_code !== 'crypto' && activeDeposit.requisite ? (
                  <>
                    {Object.entries(activeDeposit.requisite.details).map(([key, value]) => (
                      <CopyRow
                        key={key}
                        label={({
                          card_number: t('payments:cardNumber'),
                          card_holder: t('payments:cardHolderName'),
                          holder_name: t('payments:cardHolderName'),
                          recipient_name: t('payments:receiverName'),
                          bank: 'Bank',
                          bank_name: 'Bank',
                          account_number: t('payments:accountNumber'),
                          phone_number: t('payments:phoneNumber'),
                          wallet_address: t('payments:walletLabel') + ' ' + t('payments:addressLabel'),
                          address: t('payments:walletLabel') + ' ' + t('payments:addressLabel'),
                          network: t('payments:network'),
                          ss: t('payments:paymentNote'),
                          note: t('payments:paymentNote'),
                        } as Record<string, string>)[key.toLowerCase()]
                          || t(`payments:field_${key}`, { defaultValue: t('payments:extraPaymentInfo') })}
                        value={String(value)}
                        formatted={formatDetail(key, String(value))}
                      />
                    ))}
                  </>
                ) : null}
                {activeDeposit.method_code !== 'crypto' && qrUrl ? (
                  <div className="qr-wrap">
                    <button type="button" className="qr-enlarge" onClick={() => setQrOpen(true)} aria-label={t('payments:enlargeQr')}>
                      <img src={qrUrl} alt={t('payments:qrLabel')} width={220} height={220} />
                    </button>
                    <a className="btn btn-ghost btn-compact" href={qrUrl} download="vbbplay-deposit-qr.svg">
                      {t('payments:saveQr')}
                    </a>
                    {activeDeposit.crypto_network ? (
                      <p className="error-note">{t('payments:networkWarning', { network: activeDeposit.crypto_network })}</p>
                    ) : null}
                  </div>
                ) : null}
                {qrOpen && qrUrl ? (
                  <div className="qr-overlay" role="dialog" aria-modal="true">
                    <img src={qrUrl} alt={t('payments:qrLabel')} />
                    <div className="qr-overlay-actions">
                      <a className="btn btn-primary" href={qrUrl} download="vbbplay-deposit-qr.svg">
                        {t('payments:saveQr')}
                      </a>
                      <button type="button" className="btn btn-ghost" onClick={() => setQrOpen(false)}>
                        {t('payments:closeQr')}
                      </button>
                    </div>
                  </div>
                ) : null}
                {activeDeposit.method_code === 'crypto' && activeDeposit.instructions?.text ? (
                  <details className="vbb-crypto-additional"><summary>{t('payments:cryptoMoreInfo')}</summary><p>{activeDeposit.instructions.text}</p></details>
                ) : null}
                {activeDeposit.method_code !== 'crypto' && activeDeposit.instructions?.text ? <p>{activeDeposit.instructions.text}</p> : null}
                {activeDeposit.method_code !== 'crypto'
                  && activeDeposit.can_upload_receipt
                  && !activeDeposit.receipt_uploaded
                  && !activeIsTonConnect ? (
                  <label className="btn btn-ghost">
                    {t('payments:uploadReceipt')}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      hidden
                      onChange={(event) => void onReceipt(event.target.files?.[0] || null)}
                    />
                  </label>
                ) : null}
                {activeDeposit.method_code === 'crypto' && !activeIsTonConnect ? (
                  <p className="muted-note">{t('payments:cryptoAwaitingAdmin')}</p>
                ) : null}
                {activeDeposit.can_cancel ? (
                  <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void onCancel()}>
                    {t('payments:cancelDeposit')}
                  </button>
                ) : null}
                {activeDeposit.method_code !== 'crypto' ? <p className="muted-note">{t('payments:uploadReceiptReminder')}</p> : null}
              </div>
            ) : selectedDeposit === 'crypto' && !depositMethod?.enabled && !isTonConnectPair ? null : (
              <></>
            )}

            
            {/* TON Connect UI hidden from cashier; backend/safety paths remain intact. */}

            {detailOpen && actionMessage && !bankRequisiteOpen ? <p className="muted-note" role="status">{actionMessage}</p> : null}

            {detailOpen && selectedDeposit !== 'crypto' && (!CASHIER_PREVIEW_MODE || previewStep === 'amount') && !openBankDeposit && (depositMethod?.enabled || CASHIER_PREVIEW_MODE) ? (
              <div className="cashier-sticky-cta">
                <div className="cashier-summary">
                  <span>{t('payments:amount')}</span>
                  <strong>{fxReady ? formatMinor(amountDisplayMinor, displayCurrency, locale) : '—'}</strong>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  disabled={busy || !fxReady || (!CASHIER_PREVIEW_MODE && (!user || !depositMethod?.enabled)) || amountMinor <= 0}
                  onClick={() => void onDeposit()}
                >
                  {CASHIER_PREVIEW_MODE ? t('payments:continue') : t('payments:continue')} ({fxReady ? formatMinor(amountDisplayMinor, displayCurrency, locale) : '—'})
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="cashier-panel">
            {detailOpen ? <button type="button" className="cashier-back" onClick={() => { setDetailOpen(false); setActionMessage(null); }}>‹ &nbsp; {t('payments:backWithdrawalLocal')}</button> : null}
            {!detailOpen ? <>{renderRegionPicker()}
            {!user ? (
              <p className="muted-note">
                {t('payments:loginRequired')} — <Link to="/auth/login">{t('common:login')}</Link>
              </p>
            ) : null}
            {CASHIER_PREVIEW_MODE ? <p className="muted-note" role="status">{t('payments:previewWithdrawBanner')}</p> : null}
            {renderMethodGroup(t('payments:bankPayment', { defaultValue: 'Bank payment' }), withdrawBank, selectedWithdraw, setSelectedWithdraw, WITHDRAW_LABELS)}
            {renderMethodGroup(t('payments:crypto'), withdrawCrypto, selectedWithdraw, setSelectedWithdraw, WITHDRAW_LABELS)}</> : <div className="cashier-detail-heading"><span className="cashier-step-mark">{selectedWithdraw === 'crypto' ? coinIcon(wdAsset) : methodIcon(selectedWithdraw)}</span><div><h2>{selectedWithdraw === 'crypto' ? `${wdAsset} (${wdNetwork})` : t(`payments:${WITHDRAW_LABELS[selectedWithdraw] || selectedWithdraw}`)}</h2><small>{t('payments:withdrawDetailsLocal')}</small></div></div>}
            {detailOpen && <div className="amount-block">
              <p className="pair-label">{t(`payments:${WITHDRAW_LABELS[selectedWithdraw] || selectedWithdraw}`)}</p>
              <label htmlFor="withdraw-amount">{t('payments:amount')}</label>
              <input
                id="withdraw-amount"
                className="field-input"
                inputMode="decimal"
                value={amountMajor}
                onChange={(event) => setAmountMajor(event.target.value)}
                disabled={!fxReady}
              />
              {!fxReady ? (
                <p className="error-note" role="alert">{t('payments:fxUnavailable', { defaultValue: 'Valyuta məzənnəsi əlçatan deyil' })}</p>
              ) : withdrawMethod?.limits ? (
                <p className="muted-note">
                  {t('payments:minMax', {
                    min: formatLimitMinor(Number(withdrawMethod.limits.min_minor || 0), 'min'),
                    max: formatLimitMinor(Number(withdrawMethod.limits.max_minor || 0), 'max'),
                  })}
                </p>
              ) : null}
              {selectedWithdraw === 'crypto'
                ? pairChips(withdrawAssets, withdrawNets, wdAsset, wdNetwork, setWdAsset, setWdNetwork)
                : null}
              {selectedWithdraw === 'bank_card' ? <BankSelector value={withdrawBankName} onChange={setWithdrawBankName} label={t('payments:selectBankLocal')}/> : null}
              {selectedWithdraw === 'bank_card' ? (
                <label htmlFor="withdraw-holder">
                  {t('payments:holderFullNameLocal')}
                  <input
                    id="withdraw-holder"
                    className="field-input"
                    value={withdrawHolder}
                    onChange={(event) => setWithdrawHolder(event.target.value)}
                  />
                </label>
              ) : null}
              <label htmlFor="withdraw-dest">{selectedWithdraw === 'bank_card' ? t('payments:recipientCardLocal') : t('payments:destination')}</label>
              <input
                id="withdraw-dest"
                className={`field-input${!withdrawDest.trim() && actionMessage ? ' field-error' : ''}`}
                value={withdrawDest}
                onChange={(event) => setWithdrawDest(event.target.value)}
                placeholder={
                  selectedWithdraw === 'm10' ? 'M10' : selectedWithdraw === 'crypto' ? t('payments:copyAddress') : '0000 0000 0000 0000'
                }
              />
              {selectedWithdraw === 'bank_card' ? (
                <label htmlFor="withdraw-expiry" className="cashier-expiry-field">
                  {t('payments:cardExpiryLocal', { defaultValue: t('payments:cardExpiry') })}
                  <input
                    id="withdraw-expiry"
                    className="field-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={5}
                    placeholder="MM/YY"
                    value={withdrawExpiry}
                    onChange={(event) => {
                      const digits = event.target.value.replace(/\D/g, '').slice(0, 4);
                      setWithdrawExpiry(digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits);
                    }}
                  />
                </label>
              ) : null}
              <p className="muted-note">
                {t('payments:availableBalanceLocal')}:{' '}
                <strong>
                  {fxReady
                    ? formatMinor(
                        aznMinorToPlayMinorFloor(wallet?.balance_real_minor ?? 0, displayRate),
                        displayCurrency,
                        locale,
                      )
                    : '—'}
                </strong>
              </p>
            </div>}
            {detailOpen && actionMessage ? <p className="muted-note" role="status">{actionMessage}</p> : null}
            {detailOpen && <div className="cashier-sticky-cta">
              <button
                type="button"
                className="btn btn-primary btn-block"
                disabled={busy || !fxReady || (!CASHIER_PREVIEW_MODE && (!user || !withdrawMethod?.enabled)) || amountMinor <= 0}
                onClick={() => void onWithdraw()}
              >
                {CASHIER_PREVIEW_MODE ? t('payments:checkRequest') : t('payments:continue')}
              </button>
            </div>}
          </div>
        )}
      </div>
    </div>
  );
}

function HistoryPanel({
  items,
  deposits,
  withdrawals,
  message,
}: {
  items: TransactionItem[];
  deposits: DepositResponse[];
  withdrawals: WithdrawResponse[];
  message: string | null;
}) {
  const { t, i18n } = useTranslation('payments');
  const [filter, setFilter] = useState<'all' | 'deposit' | 'withdrawal'>('all');
  const visible = items.filter((item) => (filter === 'all' ? true : item.type === filter));
  const displayDate = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || 'az', { day: 'numeric', month: 'long' }).format(d);
  };
  // Preserve the full local calendar date internally so different years never merge.
  const historyDayKey = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const displayTime = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || 'az', { hour: '2-digit', minute: '2-digit' }).format(d);
  };
  const localizedStatus = (status: string) => {
    const keys: Record<string, string> = {
      awaiting_receipt: 'statusPending', pending: 'statusPending',
      pending_review: 'statusReview', reserved: 'statusReview', processing: 'statusReview',
      approved: 'statusApproved', paid: 'statusPaid',
      succeeded: 'statusCompleted', completed: 'statusCompleted', settled: 'statusCompleted',
      rejected: 'statusRejected', expired: 'statusExpired',
      canceled: 'statusCancelled', cancelled: 'statusCancelled',
      failed: 'statusFailed', failure: 'statusFailed',
    };
    return keys[status.toLowerCase()] ? t(keys[status.toLowerCase()]) : status;
  };

  // VBB_HISTORY_TIDY_20261030: preserve legitimate records while hiding abandoned drafts.
  const hiddenDraftRefs = new Set(deposits.filter(row => {
    const status = (row.display_status || row.status || '').toLowerCase();
    return (status === 'cancelled' || status === 'canceled') && !row.receipt_uploaded && !row.payer_reference_submitted;
  }).map(row => row.public_id));
  const depositRefs = new Set(deposits.map(row => row.public_id));
  const withdrawalRefs = new Set(withdrawals.map(row => row.public_id));

  const activity = [
    ...deposits.filter(row => {
      if (filter === 'withdrawal') return false;
      // Hide self-cancelled, unsubmitted draft deposits only.
      // Expired/rejected requests remain visible for auditing by the customer.
      const status = (row.display_status || row.status || '').toLowerCase();
      return !(status === 'cancelled' || status === 'canceled') || Boolean(row.receipt_uploaded || row.payer_reference_submitted);
    }).map(row => ({
      key: 'deposit-' + row.public_id, date: row.created_at || '', type: 'deposit' as const,
      amount: row.amount_minor, currency: row.currency,
      title: row.crypto_asset ? `${row.crypto_asset} (${row.crypto_network || ''})` : t('historyBankDeposit'),
      status: row.display_status || row.status, reference: row.public_id,
    })),
    ...withdrawals.filter(() => filter !== 'deposit').map(row => ({
      key: 'withdrawal-' + row.public_id, date: row.created_at || '', type: 'withdrawal' as const,
      amount: row.amount_minor, currency: row.currency,
      title: t('historyWithdrawal'), status: row.status, reference: row.public_id,
    })),
    ...visible.filter(row =>
      (row.type === 'deposit' || row.type === 'withdrawal') &&
      !(row.type === 'deposit' && row.reference && hiddenDraftRefs.has(row.reference)) &&
      !(row.type === 'deposit' && row.reference && depositRefs.has(row.reference)) &&
      !(row.type === 'withdrawal' && row.reference && withdrawalRefs.has(row.reference))
    ).map(row => ({ key: 'transaction-' + row.id, date: row.created_at || '',
      type: row.type, amount: row.amount_minor, currency: row.currency,
      title: row.type === 'withdrawal' ? t('historyWithdrawal') : t('historyDeposit'),
      status: row.status, reference: row.reference || '',
    })),
  ].sort((a,b) => Date.parse(b.date) - Date.parse(a.date));
  const groups = activity.reduce<Record<string, typeof activity>>((acc, row) => {
    const key = historyDayKey(row.date);
    (acc[key] ??= []).push(row);
    return acc;
  }, {});
  return <div className="cashier-history-v5">
    <div className="cashier-history-filter" role="group" aria-label={t('payments:transactionTypeLocal')}>
      {(['all','deposit','withdrawal'] as const).map(id => <button key={id} type="button" className={`chip${filter === id ? ' active' : ''}`} onClick={() => setFilter(id)}>{id === 'all' ? t('historyAll') : id === 'deposit' ? t('historyDeposit') : t('historyWithdrawal')}</button>)}
    </div>
    {activity.length === 0 ? <div className="cashier-history-empty"><strong>{t('historyEmptyTitle')}</strong><p>{message || t('historyEmptyDescription')}</p></div> : Object.entries(groups).map(([date, rows]) => <section key={date} className="cashier-history-day"><h3>{displayDate(rows[0]?.date || date)}</h3><div className="cashier-history-day-list">{rows.map(row => <article key={row.key} className={`cashier-history-entry${['expired','rejected','failed','failure','cancelled','canceled'].includes((row.status || '').toLowerCase()) ? ' history-negative' : ''}` }><span className={`cashier-history-glyph ${row.type === 'withdrawal' ? 'outgoing' : ''}`}>{row.type === 'withdrawal' ? '↗' : '↙'}</span><div className="cashier-history-main"><strong>{row.title}</strong><small className="cashier-history-meta">{displayTime(row.date)} · {localizedStatus(row.status)}</small></div><strong className="cashier-history-total">{row.type === 'withdrawal' ? '−' : '+'}{formatMinor(row.amount,row.currency,i18n.resolvedLanguage || i18n.language || 'az')}</strong></article>)}</div></section>)}
  </div>;

}
